<template>
  <div class="package-panel">
    <div v-show="errorResponse">
      <WarningPanel
        :title="'There\'s a problem...'"
        :msg="errorResponse"
        :severe="true"
      />
    </div>

    <slot name="summary-header" />

    <div
      class="package-panel__products"
      :class="{ 'single-column': singleColumn }"
    >
      <div
        class="package-panel__option"
        v-for="product in listMainSelectedProducts"
        :key="product.code"
      >
        <div class="package-panel__option-image-wrapper">
          <img
            class="package-panel__option-image"
            v-if="productImage(product)"
            :src="productImage(product)"
            :alt="product.title"
          />
        </div>
        <span class="package-panel__option-name" :data-testid="product.type">{{
          product.title
        }}</span>
      </div>

      <template
        v-if="showOptionalProducts && listOptionalSelectedProducts.length"
      >
        <span class="package-panel__title">Optional</span>
        <div
          v-for="product in listOptionalSelectedProducts"
          :key="product.code"
          class="package-panel__option"
          :class="{ disable_option: !product.enabled }"
          data-testid="package-panel-option"
        >
          <div class="package-panel__option-image-wrapper">
            <img
              class="package-panel__option-image"
              v-if="productImage(product)"
              :src="productImage(product)"
              :alt="product.title"
            />
          </div>
          <span
            class="package-panel__option-name"
            :data-testid="product.type"
            >{{ product.title }}</span
          >
          <!--          <span-->
          <!--              class="product-option__ribbon"-->
          <!--              v-if="-->
          <!--              product.type === 'Broadband' &&-->
          <!--                !isDefaultBroadbandProductOptionSelected-->
          <!--            "-->
          <!--          >-->
          <!--            UPGRADED-->
          <!--          </span>-->
          <span v-if="allowChangingOptional" class="product-option__toggle">
            <label class="switch">
              <input
                type="checkbox"
                :checked="product.enabled"
                v-on:change="updateProductSelection(product)"
              />
              <span class="slider round"></span>
            </label>
          </span>
        </div>
      </template>
    </div>

    <slot name="summary-footer"></slot>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import WarningPanel from "@/components/WarningPanel";
import { insertAfterLastProductWithType } from "@/utility/insertAfterLastProductWithType";

export default {
  name: "PackageSummary",
  components: {
    WarningPanel,
  },
  props: {
    joiningTenant: {
      required: false,
      default: false,
    },
    singleColumn: {
      type: Boolean,
      default: () => false,
    },
    allowChangingOptional: {
      type: Boolean,
      default: () => false,
    },
    showOptionalProducts: {
      type: Boolean,
      default: () => false,
    },
    onToggleTvLicence: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      errorResponse: null,
    };
  },
  computed: {
    ...mapState({
      products({ products }) {
        return products;
      },
      selectedProducts({ accountInformation }) {
        if (this.joiningTenant) {
          return accountInformation.products;
        } else {
          return this.showOptionalProducts
            ? this.$store.getters.selectedUnselectedProducts
            : this.$store.getters.selectedProducts;
        }
      },
    }),
    ...mapGetters({
      isDefaultBroadbandProductOptionSelected:
        "isDefaultBroadbandProductOptionSelected",
    }),
    filteredSelectedProducts() {
      return this.selectedProducts.filter(
        (product) => !product.code.includes("_NONE"),
      );
    },
    listMainSelectedProducts() {
      const base = !this.showOptionalProducts
        ? this.filteredSelectedProducts
        : this.filteredSelectedProducts.filter(
            (product) => product.optional === false,
          );

      if (!this.shouldShowFreeContentsInsurance) {
        return base;
      }

      const freeContentsInsuranceDisplay = {
        code: "FREE_CONTENTS_INSURANCE_DISPLAY",
        type: "Free Contents Insurance",
        title: "Contents Insurance",
        optional: false,
        image: "/icon-contents-insurance-alt.svg",
      };

      return insertAfterLastProductWithType(
        base,
        "Water",
        freeContentsInsuranceDisplay,
      );
    },
    listOptionalSelectedProducts() {
      return this.filteredSelectedProducts.filter(
        (product) => product.optional === true,
      );
    },
    shouldShowFreeContentsInsurance() {
      return this.$store.getters.shouldShowFreeContentsInsurance;
    },
  },
  methods: {
    ...mapMutations({
      setProductOption: "SET_PRODUCT_OPTION",
      unPickProductOption: "SET_UNPICKED_PRODUCT_OPTION",
    }),
    ...mapActions(["updatePreSignupLeadProductData"]),
    productImage(productOption) {
      if (productOption.image !== "") {
        return productOption.image;
      }

      if (!this.products || this.products.length === 0) {
        return "";
      }

      const product = this.products.find(
        (product) => product.type === productOption.type,
      );
      const option = product.options.find(
        (op) => op.image && op.image.length > 0,
      );
      return option === undefined ? "" : option.image;
    },
    updateProductSelection(productOption) {
      var vm = this;
      if (!this.allowChangingOptional) return;

      let enabled = !productOption.enabled;

      //find product type none option
      let noneProductOption = this.$store.state.products
        .filter((product) => product.type === productOption.type)[0]
        .options.filter((option) => option.code.includes("_NONE"))[0];

      if (productOption.type === "TV Licence") {
        this.onToggleTvLicence(enabled);
      }

      if (!noneProductOption) {
        this.errorResponse =
          "Service error - Unable to update your package choices, please try again later.";
        return;
      }

      this.unPickProductOption({
        unpicked: enabled ? noneProductOption : productOption,
        picked: enabled ? productOption : noneProductOption,
      });

      productOption.enabled = enabled;

      this.updatePreSignupLeadProductData({
        offerCode: this.$store.state.discount.offer,
        affiliateCode: this.$store.state.discount.aff,
        referralCode: this.$route.query.ref,
      }).then((res) => {
        //if update failed, return changes to original
        if (!(res.status === 200 || res.status === 204)) {
          vm.unPickProductOption({
            unpicked: !enabled ? noneProductOption : productOption,
            picked: !enabled ? productOption : noneProductOption,
          });

          productOption.enabled = !enabled;

          vm.errorResponse =
            "Service error - Unable to update your package choices, please try again later.";
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@mixin single-column {
  grid-template-columns: 100%;
  grid-template-rows: auto;
}

.package-panel__products {
  display: grid;
  grid-template-columns: 50% 50%;
  grid-template-rows: 1fr 1fr;
  grid-auto-flow: row;
  grid-column-gap: 1rem;
  grid-row-gap: 2rem;

  &.single-column {
    @include single-column;
  }

  @include media("<medium") {
    @include single-column;
  }
}

.package-panel__title {
  text-align: left;
  margin-top: 1rem;
  margin-bottom: -1rem;
  font-size: 16px;
}

.package-panel__option {
  display: flex;
  align-items: center;
  overflow: hidden;
  border: 3px solid $brand-blue;

  background-color: $brand-blue;
  border-top-left-radius: 27px 50%;
  border-bottom-left-radius: 27px 50%;
  border-top-right-radius: 10px;
  border-bottom-right-radius: 10px;

  &-name {
    font-size: 17px;
    text-align: left;
    color: white;
    font-weight: $font-bold;
    flex-grow: 1;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &.disable_option {
    background-color: unset;

    .package-panel__option-image-wrapper {
      border: unset;
    }

    .package-panel__option-name {
      color: $brand-blue;
    }
  }
}

.package-panel__option-image-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;

  border: 1px solid $brand-blue;
  background: white;
  border-radius: 50%;
  min-width: 50px;
  max-width: 50px;
  min-height: 50px;
  max-height: 50px;
  margin: 2px $spacing-small 2px 2px;

  .package-panel__option-image {
    width: 36px;
    height: 36px;
    max-width: 36px;
    max-height: 36px;
  }
}

.product-option__ribbon {
  justify-self: flex-end;
  margin-right: -14px;
  text-align: center;
  text-transform: uppercase;
  font-size: 10px;
  font-weight: $font-bold;
  color: $white;
  background-color: $brand-orange;
  padding: 0 18px;
  transform: rotate(45deg);
}

.product-option__toggle {
  margin-left: 15px;
  margin-right: 15px;
}

.switch {
  position: relative;
  display: inline-block;
  width: 60px;
  height: 28px;

  input {
    opacity: 0;
    width: 0;
    height: 0;

    &:checked + .slider {
      background-color: $brand-dark-blue;

      &:before {
        -webkit-transform: translateX(32px);
        -ms-transform: translateX(32px);
        transform: translateX(32px);
      }
    }

    &:focus + .slider {
      box-shadow: 0 0 1px $brand-dark-blue;
    }
  }

  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #ccc;
    -webkit-transition: 0.4s;
    transition: 0.4s;

    &:before {
      position: absolute;
      content: "";
      height: 20px;
      width: 20px;
      left: 4px;
      bottom: 4px;
      background-color: white;
      -webkit-transition: 0.4s;
      transition: 0.4s;
    }

    &.round {
      border-radius: 34px;

      &:before {
        border-radius: 50%;
      }
    }
  }
}
</style>
